import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { Sparkles, Send, Plus, Trash2, Bot, User, Loader2, MessageSquare, Mic, Square, Volume2, Headphones } from 'lucide-react';
import { API } from '@/App';
import { useVoiceRecorder, useSpeaker } from '@/hooks/useVoiceAssistant';
import { VoicePicker } from '@/components/VoicePicker';

const SUGGESTIONS = [
  "Qu'est-ce qui demande mon attention aujourd'hui ?",
  "Rédige une actualité pour annoncer le prochain congrès SFOALC",
  "Propose un modèle d'email de relance pour les adhésions expirées",
];

const VoiceStatusBar = ({ status, level, onStop }) => {
  if (status === 'idle') return null;
  const label = { listening: 'Je vous écoute… parlez, je m\'arrête quand vous faites une pause', transcribing: 'Je retranscris ce que vous avez dit…', preparing: 'Je prépare ma réponse vocale…', speaking: 'Je vous réponds à voix haute…' }[status];
  return (
    <div className="max-w-3xl mx-auto mb-2 flex items-center gap-3 px-3 py-2 rounded-xl bg-[#059669]/5 border border-[#059669]/20" data-testid={`copilot-voice-status-${status}`}>
      {status === 'listening' ? (
        <div className="flex items-end gap-0.5 h-5 w-10" aria-hidden>
          {[0.6, 1, 0.75, 0.9, 0.5].map((m, i) => (
            <span key={i} className="w-1 rounded-full bg-[#059669] transition-[height] duration-75" style={{ height: `${Math.max(15, level * 100 * m)}%` }} />
          ))}
        </div>
      ) : status === 'transcribing' || status === 'preparing' ? <Loader2 size={16} className="animate-spin text-[#059669]" /> : <Volume2 size={16} className="text-[#059669] animate-pulse" />}
      <span className="text-xs text-gray-700 flex-1">{label}</span>
      {status !== 'transcribing' && (
        <button onClick={onStop} className="text-xs font-medium text-[#059669] hover:underline flex items-center gap-1" data-testid="copilot-voice-stop-btn">
          <Square size={11} /> {status === 'listening' ? 'Terminer' : 'Arrêter'}
        </button>
      )}
    </div>
  );
};

const CopilotPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [initialSent, setInitialSent] = useState(false);
  const [voiceMode, setVoiceMode] = useState(() => localStorage.getItem('copilot-voice-mode') === '1');
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);
  const sessionRef = useRef(null);
  const streamingRef = useRef(false);
  const voiceModeRef = useRef(voiceMode);
  const recorder = useVoiceRecorder();
  const speaker = useSpeaker();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    voiceModeRef.current = voiceMode;
    localStorage.setItem('copilot-voice-mode', voiceMode ? '1' : '0');
  }, [voiceMode]);

  // initialSentRef guards against React StrictMode double-invoking effects (duplicate LLM calls)
  const initialSentRef = useRef(false);
  useEffect(() => {
    const initialMessage = location.state?.initialMessage;
    if (initialMessage && !initialSentRef.current && !initialSent) {
      initialSentRef.current = true;
      setInitialSent(true);
      navigate(location.pathname, { replace: true, state: null });
      sendMessage(initialMessage);
    }
  }, [location.state]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  useEffect(() => () => { recorder.cancel(); speaker.stop(); }, []);

  const selectSession = (id) => { sessionRef.current = id; setActiveSession(id); };

  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API}/copilot/sessions`, { headers });
      setSessions(res.data);
    } catch { }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/copilot/sessions/${sessionId}`, { headers });
      selectSession(sessionId);
      setMessages(res.data.messages || []);
    } catch { }
  };

  const newChat = () => {
    selectSession(null);
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/copilot/sessions/${sessionId}`, { headers });
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (sessionRef.current === sessionId) newChat();
    } catch { }
  };

  const readAloud = async (text, id) => {
    setVoiceStatus('preparing');
    try {
      await speaker.speak(text, id, () => setVoiceStatus('speaking'));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setVoiceStatus('idle');
    }
  };

  const listen = async () => {
    speaker.unlock();
    speaker.stop();
    setVoiceStatus('listening');
    let blob;
    try {
      blob = await recorder.start();
    } catch {
      setVoiceStatus('idle');
      toast.error("Micro inaccessible : autorisez l'accès au micro dans votre navigateur.");
      return;
    }
    if (!blob) { setVoiceStatus('idle'); return; }
    setVoiceStatus('transcribing');
    try {
      const fd = new FormData();
      fd.append('audio', blob, blob.type.includes('mp4') ? 'voice.mp4' : 'voice.webm');
      const res = await axios.post(`${API}/copilot/transcribe`, fd, { headers });
      setVoiceStatus('idle');
      const text = res.data.text?.trim();
      if (!text) { toast.info("Je n'ai rien entendu, réessayez."); return; }
      sendMessage(text, { fromVoice: true });
    } catch (e) {
      setVoiceStatus('idle');
      toast.error(e.response?.data?.detail || 'Transcription impossible');
    }
  };

  const sendMessage = async (text, { fromVoice = false } = {}) => {
    if (!text?.trim() || streamingRef.current) return;
    const userMsg = text.trim();
    setInput('');

    const sessionId = sessionRef.current || crypto.randomUUID();
    selectSession(sessionId);

    setMessages(prev => [...prev, { role: 'user', content: userMsg, voice: fromVoice }]);
    streamingRef.current = true;
    setStreaming(true);
    setStreamingText('');
    let answer = null;

    try {
      const response = await fetch(`${API}/copilot/chat`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg, session_id: sessionId, voice: voiceModeRef.current }),
      });

      if (response.status === 402) {
        setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Crédits IA insuffisants. [Rechargez votre solde](/credits) pour continuer à utiliser l\'assistant.' }]);
      } else {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullText = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          for (const line of decoder.decode(value).split('\n')) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'delta') {
                fullText += data.content;
                setStreamingText(fullText);
              } else if (data.type === 'done') {
                answer = { role: 'assistant', content: fullText, id: crypto.randomUUID() };
                setMessages(prev => [...prev, answer]);
                setStreamingText('');
              } else if (data.type === 'error') {
                setMessages(prev => [...prev, { role: 'assistant', content: `Erreur : ${data.content}` }]);
                setStreamingText('');
              }
            } catch { }
          }
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Erreur de connexion : ${err.message}` }]);
      setStreamingText('');
    }

    streamingRef.current = false;
    setStreaming(false);
    loadSessions();

    if (answer?.content && voiceModeRef.current) {
      await readAloud(answer.content, answer.id);
      if (fromVoice && voiceModeRef.current) listen();
    }
  };

  const toggleVoiceMode = () => {
    speaker.unlock();
    if (voiceMode) { speaker.stop(); recorder.cancel(); }
    setVoiceMode(v => !v);
  };

  const stopVoice = () => {
    if (voiceStatus === 'listening') recorder.stop();
    else { speaker.stop(); setVoiceStatus('idle'); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <AdminLayout>
      <div className="flex h-[calc(100vh-105px)]" data-testid="copilot-page">
        {/* Sessions sidebar */}
        <div className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-100">
          <div className="p-3 border-b border-gray-100">
            <button onClick={newChat}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#059669] text-white text-sm font-medium hover:bg-[#047857] transition-colors"
              data-testid="new-chat-btn">
              <Plus size={16} /> Nouvelle conversation
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {sessions.map(s => (
              <div
                key={s.session_id}
                role="button"
                tabIndex={0}
                onClick={() => loadSession(s.session_id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadSession(s.session_id); } }}
                className={`w-full flex items-center gap-2 px-3 py-2 mb-1 rounded-lg text-left cursor-pointer transition-colors group ${
                  activeSession === s.session_id ? 'bg-[#059669]/10 text-[#059669]' : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`session-${s.session_id}`}
              >
                <MessageSquare size={14} className="flex-shrink-0" />
                <span className="text-xs truncate flex-1">{s.preview || 'Conversation'}</span>
                <button onClick={(e) => deleteSession(s.session_id, e)} aria-label="Supprimer la conversation"
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 hover:text-red-500 transition-all"
                  data-testid={`delete-session-${s.session_id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-end gap-3 px-4 py-2 border-b border-gray-50 bg-white flex-wrap">
            <VoicePicker />
            <button
              onClick={toggleVoiceMode}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${voiceMode ? 'bg-[#059669] text-white border-[#059669]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#059669] hover:text-[#059669]'}`}
              aria-pressed={voiceMode}
              data-testid="copilot-voice-mode-toggle"
            >
              <Headphones size={14} /> Mode vocal {voiceMode ? 'activé' : 'désactivé'}
            </button>
          </div>
          {messages.length === 0 && !streamingText ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#059669] to-[#047857] flex items-center justify-center mb-4 shadow-lg">
                <Sparkles size={28} className="text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Assistant IA SFOALC</h2>
              <p className="text-sm text-gray-500 mb-6 text-center max-w-md">
                Écrivez-moi ou appuyez sur le micro pour me parler. Je vous pose des questions pour bien comprendre, puis je vous aide à rédiger, relancer ou décider.
              </p>
              <div className="grid gap-2 w-full max-w-md">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-[#059669] hover:bg-[#059669]/5 text-sm text-gray-700 transition-all"
                    data-testid={`suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => {
                const msgId = msg.id || `idx-${i}`;
                const isSpeaking = speaker.speakingId === msgId;
                return (
                  <div key={msgId} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`} data-testid={`msg-${msg.role}-${i}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-lg bg-[#059669] flex items-center justify-center flex-shrink-0">
                        <Bot size={14} className="text-white" />
                      </div>
                    )}
                    <div className={`max-w-[75%] ${msg.role === 'user' ? '' : 'min-w-0'}`}>
                      <div className={`rounded-2xl px-4 py-3 text-sm ${
                        msg.role === 'user'
                          ? 'bg-[#1e3a5f] text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-900 rounded-bl-md prose prose-sm max-w-none'
                      }`}>
                        {msg.role === 'assistant' ? <ReactMarkdown>{msg.content}</ReactMarkdown> : msg.content}
                      </div>
                      {msg.role === 'user' && msg.voice && <p className="text-[10px] text-gray-400 mt-1 text-right flex items-center justify-end gap-1"><Mic size={10} /> dicté</p>}
                      {msg.role === 'assistant' && (
                        <button
                          onClick={() => (isSpeaking ? stopVoice() : (speaker.unlock(), readAloud(msg.content, msgId)))}
                          className="mt-1 text-[11px] text-gray-400 hover:text-[#059669] flex items-center gap-1 transition-colors"
                          data-testid={`copilot-listen-btn-${i}`}
                        >
                          {isSpeaking ? <><Square size={11} /> Arrêter la lecture</> : <><Volume2 size={12} /> Écouter</>}
                        </button>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-7 h-7 rounded-lg bg-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                        <User size={14} className="text-white" />
                      </div>
                    )}
                  </div>
                );
              })}
              {streamingText && (
                <div className="flex gap-3" data-testid="streaming-msg">
                  <div className="w-7 h-7 rounded-lg bg-[#059669] flex items-center justify-center flex-shrink-0">
                    <Bot size={14} className="text-white" />
                  </div>
                  <div className="max-w-[75%] rounded-2xl rounded-bl-md bg-gray-100 text-gray-900 px-4 py-3 text-sm prose prose-sm max-w-none">
                    <ReactMarkdown>{streamingText}</ReactMarkdown>
                    <span className="inline-block w-1 h-4 bg-[#059669] animate-pulse ml-0.5" />
                  </div>
                </div>
              )}
              <div ref={messagesEnd} />
            </div>
          )}

          {/* Input */}
          <div className="border-t border-gray-100 p-3 bg-white">
            <VoiceStatusBar status={voiceStatus} level={recorder.level} onStop={stopVoice} />
            <div className="flex items-end gap-2 max-w-3xl mx-auto">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Écrivez ou appuyez sur le micro pour parler…"
                rows={1}
                className="flex-1 resize-none rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]/30 focus:border-[#059669]"
                data-testid="copilot-input"
                disabled={streaming}
              />
              <button
                onClick={() => (recorder.recording ? recorder.stop() : listen())}
                disabled={streaming || voiceStatus === 'transcribing'}
                className={`p-2.5 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${recorder.recording ? 'bg-red-500 text-white animate-pulse' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#059669] hover:text-[#059669]'}`}
                title={recorder.recording ? 'Terminer' : 'Parler à l\'assistant'}
                data-testid="copilot-mic-btn"
              >
                {recorder.recording ? <Square size={18} /> : <Mic size={18} />}
              </button>
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || streaming}
                className="p-2.5 rounded-xl bg-[#059669] text-white hover:bg-[#047857] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="copilot-send-btn"
              >
                {streaming ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CopilotPage;
